// Libraries
import React from 'react';

// Supermove
import {Styled} from '@supermove/components';
import {ResponsiveType, useEffect, useResponsive, useState} from '@supermove/hooks';
import {Typography} from '@supermove/styles';
import {NativeSyntheticEvent, TextInputContentSizeChangeEventData} from '@supermove/styles/types';

// This is a hack for getting the right height for the input.
// For some reason onContentSizeChange gives a slow height
// reduction when a line gets deleted. Eg, Instead of going
// from 55 to 36 (which would be expected) it goes 53, 51, 49,
// etc... slowly decreasing the size with each character change.
const getInputHeight = ({
  contentSizeHeight,
  paddingVertical,
  responsive,
}: {
  contentSizeHeight: number;
  paddingVertical: number;
  responsive: ResponsiveType;
}) => {
  const textLineHeight = responsive.desktop ? 20 : 24; // This is the lineHeight for the Typography
  const paddingHeight = paddingVertical * 2;
  const numberOfTextLines = Math.floor((contentSizeHeight - paddingHeight) / textLineHeight);
  const totalTextHeight = numberOfTextLines * textLineHeight;
  return totalTextHeight + paddingHeight;
};

const TextInput = Styled.TextInput<{responsive: ResponsiveType}>`
  ${Typography.Responsive.Body}
  padding-vertical: 8px;
  padding-horizontal: 12px;
`;

type ResponsiveTextInputProps = {
  value: string;
  minHeight?: number;
  maxHeight?: number;
  paddingVertical?: number;
  style?: React.CSSProperties;
};

const ResponsiveTextInput = ({
  value,
  minHeight = 36,
  maxHeight,
  paddingVertical = 8,
  style,
  ...props
}: ResponsiveTextInputProps) => {
  const responsive = useResponsive();
  const [height, setHeight] = useState(minHeight || (responsive.desktop ? 36 : 48));

  // Explicitly handle the case where the value is empty
  useEffect(() => {
    if (!value && height !== minHeight) {
      setHeight(minHeight);
    }
  }, [value, height, minHeight]);

  const handleSizeChange = (contentSizeHeight: number) => {
    const contentHeight = getInputHeight({contentSizeHeight, paddingVertical, responsive});
    const contentVsMinHeight = Math.max(minHeight, contentHeight);
    const displayHeight = maxHeight ? Math.min(contentVsMinHeight, maxHeight) : contentHeight;
    setHeight(displayHeight);
  };

  return (
    <TextInput
      multiline
      value={value}
      onContentSizeChange={(event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) =>
        handleSizeChange(event.nativeEvent.contentSize.height)
      }
      responsive={responsive}
      style={[style, {height}]}
      {...props}
    />
  );
};

export default ResponsiveTextInput;
